// articles/CaptainsChoiceSetup.js
// Event setup guide #1 (Sept 27, 2026): a Captain's Choice (scramble) event in TTO,
// start to finish, in the order the steps have to happen. Written from the live
// screens — button and tab names are quoted exactly as they appear, so if a label
// changes in App.js, change it here too.
//
// Linked from inside the app: Set up event → Scoring (One score per team), and the
// Captain's Choice (Scramble) panel in Games Setup. Public, so prospective
// organizers can read it before they sign up.

import React from 'react';
import { colors, getGuideBySlug } from '../guidesData';
import { H2, H3, P, UL, LI, GuideCTA, FAQ } from '../articleUI';
import { Step, Btn, Heads, StepTracker, PRINT_CSS, CtpSection } from './eventGuideParts';

const guide = getGuideBySlug('how-to-set-up-a-captains-choice-golf-tournament');

const FAQS = [
  {
    q: 'Do Captain’s Choice scores post to GHIN?',
    a: 'No. A scramble is not a handicap round under the Rules of Handicapping, so once a round is scored one score per team, TTO turns GHIN posting off for it: players get no posting reminder and no Post to GHIN button.',
  },
  {
    q: 'Can I play it gross, or with team handicaps?',
    a: 'Either. Choose Gross or Net (team handicap) in the Captain’s Choice game. Net uses the USGA recommended allowances (2-man 35/15, 3-man 30/20/10, 4-man 25/20/15/10, lowest handicap first), which you can change, and you can type in any team’s handicap yourself.',
  },
  {
    q: 'What if a team is short a player?',
    a: 'Keep them as a smaller team on the Pairings page. On a net game TTO gives that team the allowance for its actual size and flags it. In a scramble, a player on the short team usually hits a second ball in rotation; a blind draw doesn’t work in this format.',
  },
  {
    q: 'Can I change the teams after I print?',
    a: 'Yes, but reprint. Each team’s code, scorecard and cart sign are tied to its team number, so after changing pairings, issue codes again for any team that needs one and reprint its card and sign.',
  },
  {
    q: 'Does every player need the app?',
    a: 'No. Only the player keeping the card enters scores. Players can open the team card from their TTO link or by scanning the QR code on the printed scorecard, and anyone can follow along on the live leaderboard.',
  },
  {
    q: 'How are ties settled?',
    a: 'You choose in the game: a USGA scorecard playoff (last 9, 6, 3, then the 18th), hole handicap, or "We’ll decide", where you put the tied teams in order yourself. Only ties for the places you pay are decided.',
  },
];

export default function CaptainsChoiceSetup() {
  return (
    <article>
      <h1
        style={{
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontSize: 38,
          fontWeight: 400,
          color: colors.gray900,
          margin: '0 0 8px',
          lineHeight: 1.15,
        }}
      >
        How to Set Up a Captain&rsquo;s Choice Golf Tournament
      </h1>
      <style>{PRINT_CSS}</style>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '0 0 28px' }}
      >
        <p style={{ fontSize: 15, color: colors.gray400, margin: 0 }}>
          {guide.readMinutes} min read &middot; Event setup guide
        </p>
        <button
          type="button"
          className="guide-no-print"
          onClick={() => window.print()}
          style={{
            background: 'white',
            color: colors.green800,
            border: `1px solid ${colors.gray300}`,
            borderRadius: 8,
            padding: '8px 14px',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          🖨️ Print this guide
        </button>
      </div>

      <P>
        A Captain&rsquo;s Choice (also called a scramble) is the most popular golf tournament format
        there is: every player on the team hits, the team picks the best shot, everyone plays from
        there, and the team records one score per hole. This guide walks you through setting up a
        Captain&rsquo;s Choice golf tournament in Tee Time Organizer, from the first click to the final
        results, in the order the steps need to happen.
      </P>

      <StepTracker
        storageKey="tto_guide_captains_choice_steps"
        steps={[
          'Create the event',
          'Set up the event',
          'Invite players',
          'Starting holes & teams',
          "Captain's Choice game",
          'Issue team codes',
          'Print cards & signs',
          'Play & leaderboard',
          'Cards, ties & finalize',
        ]}
        note="Steps 1 to 3 can be done weeks ahead; steps 4 to 7 wait until the field is set. Tap a step to jump to it."
      />

      <H2>Before you start</H2>
      <P>Settle these decisions first. Each one is entered in a later step, but you&rsquo;ll need the answers there.</P>
      <UL>
        <LI>
          <strong>Each event needs an Event Pass</strong> (unless events are included in your
          plan). Where the <Btn>This is an event</Btn> checkbox would be, you&rsquo;ll see{' '}
          <Btn>🔒 This is an event · Get an Event Pass</Btn>. Buy the pass there and the checkbox
          opens; the pass also switches on scoring and the event games. Running your very first
          event? Start with{' '}
          <a href="/guides/how-to-run-a-golf-tournament-with-tee-time-organizer" style={{ color: colors.green700 }}>
            Setting Up Your Tournament
          </a>.
        </LI>
        <LI>
          <strong>Who is invited:</strong> your whole player list, or only the players you choose
          (step 3).
        </LI>
        <LI>
          <strong>Shotgun or tee times</strong>, and for a shotgun, how many teams start on each hole
          (step 4).
        </LI>
        <LI>
          <strong>Team size:</strong> 2-man, 3-man or 4-man (step 5).
        </LI>
        <LI>
          <strong>Gross or net.</strong> Net uses team handicaps built from each player&rsquo;s
          Handicap Index (step 5).
        </LI>
        <LI>
          <strong>How many places you pay</strong>, and how ties are broken. TTO decides ties only for
          the places you pay, so it needs this number before scores come in (step 5).
        </LI>
      </UL>

      <H2>Setup, step by step</H2>

      <Step n={1} title="Create the tee time and mark it as an event" when="Any time before registration opens">
        <UL>
          <LI>
            On your dashboard, in <Btn>Create Tee Time</Btn>, fill in the date, time, course and
            slots.
          </LI>
          <LI>
            <strong>Start Type:</strong> choose <Btn>Shotgun</Btn> if all teams start at once. You
            assign the starting holes later, on the Pairings page (step 4).
          </LI>
          <LI>
            <strong>Inviting only selected players?</strong> Tick <Btn>Private Tee Time</Btn> (see
            step 3 for why).
          </LI>
          <LI>
            Tick <Btn>This is an event</Btn> and click <Btn>Add Tee Time</Btn>. The checkbox appears
            for single tee times, not recurring or bulk ones.
          </LI>
          <LI>
            The <Btn>Set up event</Btn> window opens on its own. For an existing tee time, use{' '}
            <Btn>✏️ Edit</Btn>, tick <Btn>This is an event</Btn>, then <Btn>Set up event ▸</Btn>.
          </LI>
        </UL>
      </Step>

      <Step n={2} title="Set up the event" when="Right after step 1">
        <UL>
          <LI>
            <strong>Event name</strong> (the only required field). It becomes the title on the
            leaderboard, scorecards and cart signs.
          </LI>
          <LI>
            <strong>Live leaderboard address</strong>: made from the name; shorten it if you like.
            It&rsquo;s printed on the cards, so settle it before you print.
          </LI>
          <LI>
            <strong>Event logo</strong> (optional): shown on the leaderboard, scorecards and cart
            signs.
          </LI>
          <LI>
            <strong>Scoring</strong>: choose <Btn>One score per team</Btn>. GHIN posting eligible
            switches to <strong>No</strong> by itself.
          </LI>
          <LI>
            Click <Btn>Save event</Btn>. If you came in through <Btn>✏️ Edit</Btn>, also click{' '}
            <Btn>Save Changes</Btn> on the tee time.
          </LI>
        </UL>
        <Heads>
          <strong>Places paid and tie-breaking are not on this screen.</strong> You set them with the
          game in step 5. Have your payout plan decided by then.
        </Heads>
      </Step>

      <Step n={3} title="Invite players" when="When you're ready to open registration">
        <P>
          <Btn>📣 Publish</Btn> on the tee time card makes the event live. How you publish decides who
          hears about it.
        </P>
        <H3>Option A: invite your whole player list</H3>
        <UL>
          <LI>
            <Btn>📣 Publish</Btn> sends a notice to every player on your list, and the event appears in
            each player&rsquo;s app, where they can RSVP.
          </LI>
          <LI>
            Choosing <Btn>Send No Notifications</Btn> skips the notice, but the event still appears in
            your players&rsquo; apps and they can still RSVP.
          </LI>
        </UL>
        <H3>Option B: invite only the players you choose (recommended)</H3>
        <UL>
          <LI>
            Make sure the tee time is marked <Btn>Private Tee Time</Btn>. A private event does not
            appear in your players&rsquo; apps unless they have been invited and have accepted the
            invitation to play. Everyone playing sees it; the rest of your list doesn&rsquo;t.
          </LI>
          <LI>
            <Btn>📣 Publish</Btn> it with <Btn>Send No Notifications</Btn>, which gives{' '}
            <Btn>Publish (No Notifications)</Btn>.
          </LI>
          <LI>
            Click <Btn>🎟️ Invite</Btn> on the tee time card and copy the invite link.
          </LI>
          <LI>
            Put the link in an invitation email you write yourself and send it to the players you
            want.
          </LI>
          <LI>
            A player who opens the link is registered. Anyone who isn&rsquo;t already in your TTO
            player list is added as a guest.
          </LI>
        </UL>
        <P>Registration is individual. You form the teams in step 4.</P>
      </Step>

      <Step n={4} title="Set the starting holes and build the teams" when="Once the field is set, on the Pairings page">
        <Heads>
          <strong>Order matters.</strong> For a shotgun, set up the starting holes <em>first</em>,
          then fill them with teams. Your saved pairings <em>are</em> your teams: the game&rsquo;s
          team list, team codes, scorecards and cart signs all come from them, and none of those
          work until pairings are saved.
        </Heads>

        <H3>4a. Set up the shotgun (skip for a tee-times start)</H3>
        <UL>
          <LI>
            Open the <strong>Pairings</strong> page for the event and turn on{' '}
            <Btn>🎯 Shotgun Start</Btn>. The <strong>⚡ Shotgun Setup</strong> window opens. Later
            you can reopen it with <Btn>⚡ Edit Setup</Btn>.
          </LI>
          <LI>
            <strong>Group size:</strong> the number of players in each starting group. Pick 2 for
            a 2-man event, 4 for a 4-man event.
          </LI>
          <LI>
            <strong>Presets</strong> fill in the list of starting holes:
            <UL>
              <LI>
                <Btn>Standard (18)</Btn>: one group on every hole. Use it for a full field.
              </LI>
              <LI>
                <Btn>Modified (9)</Btn>: one group on each of holes 1 to 9.
              </LI>
              <LI>
                <Btn>Fit to players</Btn>: only as many groups as your field needs, filled from hole
                1 and then backwards (18, 17, 16 …). Hole 1 clears quickly and everyone finishes near
                the clubhouse. This is the usual choice for a partial field.
              </LI>
            </UL>
          </LI>
          <LI>
            <strong>Adjust by hand:</strong> use <strong>+</strong> and <strong>−</strong> on a hole
            to change its number of groups. Two groups on one hole start as A and B (for example 1A
            and 1B). <strong>✕</strong> removes a hole and <Btn>+ Add Hole</Btn> puts one back.
          </LI>
          <LI>
            Check the capacity line. It should read <strong>✓ Fits N players</strong>, not{' '}
            <strong>over capacity</strong>. Then click <Btn>Create N Groups</Btn>.
          </LI>
        </UL>

        <H3>4b. Fill the teams</H3>
        <UL>
          <LI>
            <strong>Shotgun:</strong> the green <strong>Shotgun Structure</strong> bar has fill
            buttons:
            <UL>
              <LI>
                <strong>Foursomes:</strong> <Btn>⚖️ Balanced</Btn>, <Btn>🎲 Random</Btn> or{' '}
                <Btn>⛳ HCP</Btn>.
              </LI>
              <LI>
                <strong>Twosomes:</strong> <Btn>⚖️ Balanced</Btn> or <Btn>🎲 Random</Btn>.
              </LI>
            </UL>
            <strong>Balanced</strong> spreads the handicaps so each team has a fair mix (A with D, B
            with C). Twosomes need an even number of players.
          </LI>
          <LI>
            <strong>Tee-times start:</strong> use <Btn>⚖️ Balanced Flights ▾</Btn> &rarr; Balanced
            Twosomes or Balanced Foursomes.
          </LI>
          <LI>
            <strong>Teams that signed up together:</strong> move the players into the same group by
            hand.
          </LI>
          <LI>
            Click <Btn>💾 Save Pairings</Btn> to save the teams and show the pairings in each
            player&rsquo;s app. Use{' '}
            <Btn>Save without sharing</Btn> to hold them back, for example until a reception the
            night before.
          </LI>
        </UL>
      </Step>

      <Step n={5} title="Add the Captain's Choice game" when="After pairings are saved">
        <Heads>
          <strong>Every player needs a complete profile before the round.</strong> Each player&rsquo;s
          profile must include a <strong>preferred tee</strong> and a <strong>Handicap Index</strong>,
          so that his course handicap can be determined.
          The tee sets the hole handicaps printed on the team&rsquo;s card, and a net game builds the
          team handicap from the indexes. Check them in Admin &rarr; Players &rarr; edit each player,
          including guests added from your invite link. You can add or change them any time up to
          the start of the event, but they must be filled in by then.
        </Heads>
        <UL>
          <LI>
            Click <Btn>🎮 Games</Btn> on the tee time card and tick{' '}
            <strong>Captain&rsquo;s Choice (Scramble)</strong>.
          </LI>
          <LI>
            <strong>Team size:</strong> 2-man, 3-man or 4-man.
          </LI>
          <LI>
            <strong>Scoring:</strong> Gross, or Net (team handicap). For Net, the allowance
            percentages are filled in from the USGA recommendations, lowest handicap first. Change
            them if your event uses different ones; <strong>Reset</strong> puts them back.
          </LI>
          <LI>
            <strong>Places paid:</strong> enter how many places you pay. TTO breaks ties for those
            places only; ties below them stay tied.
          </LI>
          <LI>
            <strong>How ties are decided:</strong>
            <UL>
              <LI>
                <strong>Scorecard playoff (USGA):</strong> best last 9, then last 6, last 3, then
                the 18th.
              </LI>
              <LI>
                <strong>Hole handicap:</strong> the hardest holes first.
              </LI>
              <LI>
                <strong>We&rsquo;ll decide:</strong> you put the tied teams in order yourself, for
                example after a real playoff.
              </LI>
            </UL>
          </LI>
          <LI>
            Click <Btn>Save Games</Btn>. On a Net game, <strong>Show teams</strong> lists every
            team&rsquo;s handicap. Type a number in a team&rsquo;s box to set it yourself.
          </LI>
        </UL>
        <Heads>
          <strong>Net games:</strong> handicap indexes are captured when you save. Open the game and
          save again shortly before the round, and again any time you change the teams or a
          player&rsquo;s index, so the team handicaps are current.
        </Heads>
        <CtpSection team />
      </Step>

      <Step n={6} title="Issue the team codes" when="After the game is saved, before printing">
        <UL>
          <LI>
            Go to the <Btn>🖨️ Print</Btn> tab, choose the round, open <Btn>Team codes…</Btn> and
            click <Btn>Issue codes for N teams</Btn>.
          </LI>
          <LI>
            Each team gets a six-digit code and a matching QR code. They are printed on the
            team&rsquo;s scorecard and cart sign.
          </LI>
        </UL>
        <H3>How players use the QR code</H3>
        <UL>
          <LI>
            A player points his phone&rsquo;s camera at the QR code on the scorecard or cart sign
            and taps the link that appears. No app, password or sign-in is needed.
          </LI>
          <LI>
            The team&rsquo;s card opens and asks <strong>Who&rsquo;s keeping the card?</strong> He
            taps his name and enters the team&rsquo;s score each hole.
          </LI>
          <LI>
            Teammates can scan the same code to follow the card, or take it over if the scorer&rsquo;s
            phone dies.
          </LI>
          <LI>
            If the camera won&rsquo;t scan, he can type the web address printed under the QR code
            and enter the six-digit code.
          </LI>
          <LI>
            Codes work from the day before the round through the day after, and stop once the round
            is finalized.
          </LI>
        </UL>
        <Heads>
          <strong>Issue the codes before you print scorecards.</strong> A scorecard only carries the
          team&rsquo;s QR code and team code if the code existed when the card was printed.
        </Heads>
      </Step>

      <Step n={7} title="Print scorecards and cart signs" when="The day before or the morning of">
        <UL>
          <LI>
            In <Btn>🖨️ Print</Btn>: <Btn>Print scorecards</Btn> (one per team, with the team&rsquo;s
            QR code and starting hole) and <Btn>Print cart signs</Btn> (riders&rsquo; names and
            starting hole). The tee sheet, event sign and posting sheets are there too.
          </LI>
          <LI>
            If you change a team after printing, issue its code again if needed and reprint its card
            and sign.
          </LI>
        </UL>
      </Step>

      <H2>On the day</H2>

      <Step n={8} title="Play and follow the leaderboard" when="During the round">
        <UL>
          <LI>
            <strong>Keeping score:</strong> players already in your TTO player list get a link to
            their TTO app in their reminder email, where the round opens as the team&rsquo;s card. Anyone can
            also use the QR code on the scorecard (step 6). The first teammate to save keeps the
            card; the others can watch or <strong>Take over</strong>.
          </LI>
          <LI>
            <strong>Leaderboard:</strong> an event&rsquo;s leaderboard is shared automatically at its
            address. For the clubhouse TV, open <strong>📺 TV view</strong> from the Live
            Leaderboard box on the Scoring tab (or <Btn>📺 TV</Btn> on the tee time card).
          </LI>
          <LI>
            <strong>Helpers:</strong> to let pro shop staff run the desk for this one event,
            without access to your dashboard, create an event desk code in{' '}
            <Btn>Set up event</Btn> &rarr; <strong>Event desk access</strong>.
          </LI>
        </UL>
      </Step>

      <Step n={9} title="Check the cards, settle ties and finalize" when="When the last card is in">
        <H3>When the cards come in</H3>
        <UL>
          <LI>
            Check each team&rsquo;s signed card against what is in TTO. Scores kept on a phone during
            the round are already there.
          </LI>
          <LI>
            Open Admin &rarr; <strong>⛳ Scoring</strong> &rarr; <strong>⛳ Post-Round Score Entry</strong>{' '}
            and choose the round. Each team has a tab with one score row. It also says whose phone
            kept the card, or that no one kept it on a phone.
          </LI>
          <LI>
            <strong>To correct a hole</strong>, click it and type the right team score, then click{' '}
            <Btn>Save Team N</Btn>. The leaderboard updates straight away.
          </LI>
          <LI>
            <strong>A team that didn&rsquo;t keep its card on a phone:</strong> enter the whole card
            the same way.
          </LI>
          <LI>
            <strong>Already finalized?</strong> Team cards are locked once the round is final. Click{' '}
            <Btn>↶ Reopen Round</Btn> in <Btn>🎮 Games</Btn>, fix the card, then finalize again.
          </LI>
          <LI>
            Helpers at the event desk can do the same from its <strong>Scores</strong> tile.
          </LI>
        </UL>

        <H3>Ties and finalizing</H3>
        <UL>
          <LI>
            With <strong>Scorecard playoff</strong> or <strong>Hole handicap</strong>, TTO breaks ties
            for the places you pay on its own, and the leaderboard marks them.
          </LI>
          <LI>
            With <strong>We&rsquo;ll decide</strong>, or after a real playoff, open{' '}
            <Btn>🎮 Games</Btn> &rarr; <strong>Ties to decide</strong>. Put the teams in order, click{' '}
            <Btn>Use this order</Btn>, and save.
          </LI>
          <LI>
            Click <Btn>🏁 Finalize Round &amp; Save Settlements</Btn> (a Captain&rsquo;s Choice saves
            no money, only results). The results stay on the leaderboard address and in each
            player&rsquo;s app, and the team cards lock. Need a correction?{' '}
            <Btn>↶ Reopen Round</Btn>, fix it, and finalize again.
          </LI>
        </UL>
      </Step>

      <H2>What locks, and when</H2>
      <UL>
        <LI>
          <strong>After the first team score:</strong> team size, gross/net, the percentages and the
          handicap snapshot are locked. A team&rsquo;s handicap override and how ties are decided can
          still change.
        </LI>
        <LI>
          <strong>Switching back to individual scoring:</strong> not possible while a Captain&rsquo;s
          Choice game is on the round or once team scores exist. Remove the game first.
        </LI>
        <LI>
          <strong>After finalizing:</strong> team cards and team codes stop accepting scores until
          you reopen the round.
        </LI>
      </UL>


      <FAQ id="captains-choice-setup" faqs={FAQS} />
      <div className="guide-no-print">
        <GuideCTA />
      </div>
    </article>
  );
}
